import React from "react";
import { Box, Typography, Breadcrumbs, Link, Chip, Stack, useTheme, Divider } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import LayersIcon from "@mui/icons-material/Layers";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import LinkIcon from "@mui/icons-material/Link";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { getLessonMetadata } from "../../utils/lessonMetadata";

// Properly title-case a string (handles "multithreading in python" → "Multithreading In Python")
function toTitleCase(str) {
  if (!str) return "";
  return str
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function LessonHeader({ subject, contentId, title, contentText, focusMode, onToggleFocusMode }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const metadata = getLessonMetadata(subject, contentId, contentText);

  // Capitalize subject for display
  const displaySubject = subject ? toTitleCase(subject) : "";

  // Derive module number (e.g. from "14.2" → "14")
  const moduleNumber = contentId && contentId.includes(".")
    ? contentId.split(".")[0]
    : contentId;

  // Clean and properly title-case the lesson title
  const rawTitle = title
    ? title
        .replace(/^\u2705\s*/, "")
        .replace(/\(Full Detailed Guide\)/i, "")
        .trim()
    : "";
  const displayTitle = toTitleCase(rawTitle);

  // Difficulty chip colors
  const difficultyColor = {
    Beginner: { bg: "rgba(0,200,83,0.1)", border: "rgba(0,200,83,0.4)", text: "#00c853" },
    Intermediate: { bg: "rgba(255,160,0,0.1)", border: "rgba(255,160,0,0.4)", text: "#ff9800" },
    Advanced: { bg: "rgba(229,9,20,0.1)", border: "rgba(229,9,20,0.4)", text: "#e50914" },
  }[metadata.difficulty] || { bg: "rgba(255,160,0,0.1)", border: "rgba(255,160,0,0.4)", text: "#ff9800" };

  return (
    <Box sx={{ mb: 4 }}>
      {/* ── Breadcrumbs ── */}
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: 14, color: theme.palette.text.disabled, verticalAlign: "middle" }} />}
        aria-label="breadcrumb"
        sx={{
          mb: 2.5,
          "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap", alignItems: "center" },
          "& .MuiBreadcrumbs-li": { display: "flex", alignItems: "center" },
          "& .MuiBreadcrumbs-separator": { display: "flex", alignItems: "center" }
        }}
      >
        <Link
          component={RouterLink}
          to={`/${subject}`}
          underline="none"
          sx={{
            fontSize: "0.82rem",
            fontWeight: 600,
            color: theme.palette.text.secondary,
            letterSpacing: 0.3,
            "&:hover": { color: isDark ? "#ffa4a2" : "#e50914" },
            transition: "color 0.2s",
            display: "flex",
            alignItems: "center",
          }}
        >
          {displaySubject}
        </Link>
        <Typography
          sx={{
            fontSize: "0.82rem",
            fontWeight: 600,
            color: theme.palette.text.secondary,
            display: "flex",
            alignItems: "center",
          }}
        >
          Module {moduleNumber}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.82rem",
            fontWeight: 700,
            color: isDark ? "#ffa4a2" : "#e50914",
            maxWidth: 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
          }}
        >
          {displayTitle}
        </Typography>
      </Breadcrumbs>

      {/* ── Main Title ── */}
      <Typography
        variant="h1"
        sx={{
          fontSize: { xs: "2rem", md: "2.6rem" },
          fontWeight: 950,
          mb: 1.5,
          lineHeight: 1.15,
          letterSpacing: "-0.04em",
          background: "linear-gradient(135deg, #e50914 0%, #6a0dad 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {displayTitle}
      </Typography>

      {/* ── Meta Row: chips + prerequisites ── */}
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        gap={1}
        sx={{ mb: 2.5 }}
      >
        {/* Duration */}
        <Chip
          icon={<AccessTimeIcon sx={{ fontSize: "12px !important" }} />}
          label={metadata.duration}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.76rem",
            height: 26,
            borderRadius: "6px",
            bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
            color: theme.palette.text.secondary,
            border: "none",
            "& .MuiChip-icon": { color: `${theme.palette.text.secondary} !important` },
          }}
        />

        {/* Difficulty */}
        <Chip
          icon={<SignalCellularAltIcon sx={{ fontSize: "12px !important" }} />}
          label={metadata.difficulty}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.76rem",
            height: 26,
            borderRadius: "6px",
            bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
            color: difficultyColor.text,
            border: "none",
            "& .MuiChip-icon": { color: `${difficultyColor.text} !important` },
          }}
        />

        {/* Module */}
        <Chip
          icon={<LayersIcon sx={{ fontSize: "12px !important" }} />}
          label={`Module ${moduleNumber}`}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.76rem",
            height: 26,
            borderRadius: "6px",
            bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
            color: theme.palette.text.secondary,
            border: "none",
            "& .MuiChip-icon": { color: `${theme.palette.text.secondary} !important` },
          }}
        />

        {/* Focus Mode */}
        {onToggleFocusMode && (
          <Chip
            icon={focusMode ? <FullscreenExitIcon sx={{ fontSize: "13px !important" }} /> : <FullscreenIcon sx={{ fontSize: "13px !important" }} />}
            label={focusMode ? "Exit Focus" : "Focus Mode"}
            size="small"
            onClick={onToggleFocusMode}
            sx={{
              fontWeight: 600,
              fontSize: "0.76rem",
              height: 26,
              borderRadius: "6px",
              bgcolor: focusMode ? "rgba(229,9,20,0.12)" : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
              color: focusMode ? "#e50914" : theme.palette.text.secondary,
              border: focusMode ? "1px solid rgba(229,9,20,0.25)" : "none",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "& .MuiChip-icon": { color: `${focusMode ? "#e50914" : theme.palette.text.secondary} !important` },
              "&:hover": {
                bgcolor: focusMode ? "rgba(229,9,20,0.18)" : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)"),
              },
            }}
          />
        )}

        {/* Prerequisites */}
        {metadata.prerequisites && metadata.prerequisites !== "None" && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <LinkIcon sx={{ fontSize: 14, color: theme.palette.text.disabled }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: "0.8rem" }}
              >
                Prerequisite:
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.primary, fontSize: "0.8rem", fontWeight: 500 }}
              >
                {metadata.prerequisites}
              </Typography>
            </Box>
          </>
        )}
      </Stack>

      {/* ── Learning Objectives Card ── */}
      <Box
        sx={{
          borderRadius: "12px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)"}`,
          overflow: "hidden",
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            background: isDark
              ? "linear-gradient(135deg, rgba(229,9,20,0.12) 0%, rgba(106,13,173,0.08) 100%)"
              : "linear-gradient(135deg, rgba(229,9,20,0.08) 0%, rgba(106,13,173,0.04) 100%)",
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 18, lineHeight: 1 }}>🎯</Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              fontSize: "0.95rem",
              letterSpacing: 0.2,
              color: isDark ? "#ffa4a2" : "#e50914",
            }}
          >
            Learning Objectives
          </Typography>
          <Box sx={{ ml: "auto" }}>
            <Chip
              label={`${metadata.objectives.length} goals`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.72rem",
                fontWeight: 700,
                bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                color: theme.palette.text.secondary,
                border: "none",
              }}
            />
          </Box>
        </Box>

        {/* Objectives list */}
        <Box sx={{ p: 2.5, pt: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mb: 1,
              fontSize: "0.85rem",
              fontStyle: "italic",
            }}
          >
            After completing this lesson, you will be able to:
          </Typography>

          <Stack spacing={1.25}>
            {metadata.objectives.map((obj, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 1.5,
                  py: 0.25,
                  px: 1.5,
                  borderRadius: "8px",
                  bgcolor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0,0,0,0.015)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(229,9,20,0.05)" : "rgba(229,9,20,0.03)",
                    borderColor: isDark ? "rgba(229,9,20,0.25)" : "rgba(229,9,20,0.15)",
                  },
                }}
              >
                <CheckCircleIcon
                  sx={{
                    color: isDark ? "#66bb6a" : "#2e7d32",
                    fontSize: 18,
                    mt: "3px",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.6,
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    fontSize: "0.88rem",
                    textAlign: "left",
                    flexGrow: 1,
                  }}
                >
                  {obj}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
